'use client';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 무한 스크롤 — **게시판을 뺀 모든 목록의 규칙**(2026-09-02 사용자 확정).
 *
 * 쪽 번호를 누르게 하지 않고 스크롤이 끝에 닿으면 다음 묶음을 이어 붙인다.
 * 강좌 목록이 쓰던 얼개(`CourseList`)를 그대로 옮겨 왔다 — 화면마다 다시 짜면
 * 같은 함정(감시선이 여러 번 울려 한 번에 여러 쪽을 받는 것)에 다시 빠진다.
 *
 * 지켜야 할 세 가지가 여기 들어 있다.
 *  1. **한 번에 한 묶음만** 청한다(`asking`) — 감시선은 여러 번 울린다.
 *  2. **받은 줄이 한 묶음보다 적으면 그것이 마지막**이다(`atEnd`).
 *     `rows.length >= total` 로만 보면 그 수가 따라오기 전에 다음 쪽을 계속 청한다.
 *  3. 거르개(기간·탭)가 바뀌면 **처음부터 다시** 받는다(`resetKey`).
 */
export function useInfiniteRows<T>({ size, resetKey, load }: {
  /** 한 묶음 크기 */
  size: number;
  /** 이 값이 바뀌면 처음부터 다시 받는다 — 기간·탭처럼 목록을 갈아 끼우는 조건 */
  resetKey: string;
  /**
   * 한 묶음 받기 — 실패하면 `null` 을 준다(직전 목록을 그대로 둔다).
   * **`useCallback` 으로 고정해서 넘긴다** — 렌더마다 새 함수를 주면 첫 묶음을 끝없이 다시 받는다.
   */
  load: (page: number) => Promise<{ rows: T[]; total: number } | null>;
}) {
  const [rows, setRows] = useState<T[] | null>(null);
  const [total, setTotal] = useState(0);
  const [appending, setAppending] = useState(false);
  /** 마지막 묶음까지 받았나 — 그리기에 쓰므로 상태다(감시선을 끈다) */
  const [done, setDone] = useState(false);
  const page = useRef(1);
  /** 지금 한 묶음을 받는 중인가 — 감시선이 여러 번 울려도 한 번만 청한다 */
  const asking = useRef(false);
  /** `done` 의 짝 — 콜백 안에서 곧바로 봐야 해서 따로 둔다(상태는 다음 렌더에나 바뀐다) */
  const atEnd = useRef(false);
  const fetchPage = useCallback(async (n: number, append: boolean) => {
    if (asking.current) return;
    asking.current = true;
    if (append) setAppending(true);
    else setRows(null); // 갈아 끼우는 중 — 화면은 뼈대를 그린다
    try {
      const next = await load(n);
      if (next) {
        if (next.rows.length < size) { atEnd.current = true; setDone(true); }
        setTotal(next.total);
        setRows((cur) => (append && cur ? [...cur, ...next.rows] : next.rows));
      }
    } finally {
      asking.current = false;
      setAppending(false);
    }
  }, [load, size]);

  useEffect(() => {
    // 거르개가 바뀌면 처음부터 — 상태 갱신은 비동기 안에서 한다(효과 몸통의 동기 setState 금지)
    void (async () => {
      page.current = 1;
      atEnd.current = false;
      asking.current = false;
      setDone(false);
      await fetchPage(1, false);
    })();
  }, [resetKey, fetchPage]);

  const more = useCallback(() => {
    if (atEnd.current || asking.current) return;
    page.current += 1;
    void fetchPage(page.current, true);
  }, [fetchPage]);

  return { rows, total, appending, done, more };
}

/**
 * 무한 스크롤 감시선 — 화면에 들어오면 다음 묶음을 청한다(80px 앞당겨).
 *
 * 다 받았으면 **아무것도 그리지 않는다**(2026-08-22 사용자 지시 — '마지막입니다' 삭제).
 * 목록이 끝난 것은 더 나올 게 없다는 것으로 이미 보인다. 감시용 칸은 남겨 둔다 —
 * 목록이 늘어나 다시 관측 대상이 되면 이어 받아야 한다.
 */
export function InfiniteSentinel({ done, loading, more }: {
  done: boolean;
  loading: boolean;
  more: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (done) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading) more();
    }, { rootMargin: '80px' });
    io.observe(el);
    return () => io.disconnect();
  }, [done, loading, more]);
  return <div ref={ref} className={`list-sentinel${done ? ' is-done' : ''}`} aria-hidden />;
}
